import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Calculator, Truck, Train, Plane, Ship, Package, MapPin, BarChart3, TrendingUp, Clock, X, Lightbulb, Zap, Download, BookOpen, ChevronDown, ChevronUp, Info, Navigation, Shield } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, ScatterChart, Scatter, AreaChart, Area, ComposedChart, RadialBarChart, RadialBar, Treemap } from 'recharts';
import { format, subDays, subMonths, subYears, parseISO, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import '@/App.css';
import MetricsGrid from './dashboard/MetricsGrid';
import DashboardHeader from './dashboard/DashboardHeader';
import ChartTabs from './dashboard/ChartTabs';
import {
  usePalette, ShareList, ChartTooltip, ChartHeader, axisProps, fmtNumber, fmtAxis, fmtShare,
} from './charts/chartKit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardContent = ({ shipments, onShipmentsChange, loadingShipments, scope3RefreshTick, onScope3Changed }) => {
  const [loading, setLoading] = useState(false);
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [scatterData, setScatterData] = useState(null);
  const [activeChart, setActiveChart] = useState('overview');
  const [dateRange, setDateRange] = useState('all'); // all, 30days, 90days, 1year
  const [categoryFilters, setCategoryFilters] = useState({
    upstream: true,
    downstream: true,
    company_owned: true
  });

  // Scope 3 Cat 6/7 — fetch the summary AND the full activities list so we
  // can break it down by mode (flights vs cars vs rail etc.) and show a
  // recent-activity table. Refreshes when scope3RefreshTick bumps (i.e.
  // whenever the user saves a new Cat 6/7 activity anywhere in the app).
  const [scope3Summary, setScope3Summary] = useState({
    total_co2e_kg: 0, entry_count: 0, by_category: {}, by_mode: {},
  });
  const [scope3Activities, setScope3Activities] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    const opts = { signal: controller.signal };
    Promise.all([
      axios.get(`${API}/scope3/activities/summary`, opts).catch(() => ({ data: null })),
      axios.get(`${API}/scope3/activities?limit=500`, opts).catch(() => ({ data: [] })),
    ]).then(([sumRes, actRes]) => {
      setScope3Summary(sumRes.data || { total_co2e_kg: 0, entry_count: 0, by_category: {}, by_mode: {} });
      setScope3Activities(Array.isArray(actRes.data) ? actRes.data : []);
    }).catch(() => {});
    return () => controller.abort();
  }, [shipments, scope3RefreshTick]);

  // Delete a scope3 activity (trash icon in recent-activity table)
  const handleDeleteActivity = async (id) => {
    if (!window.confirm('Delete this Scope 3 activity?')) return;
    try {
      await axios.delete(`${API}/scope3/activities/${id}`);
      toast.success('Deleted');
      onScope3Changed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // Aggregates the dashboard needs: per-category totals (merging shipments
  // Cat 4/9 with scope3 activities Cat 6/7) and per-mode breakdowns for Cat 6/7.
  const scope3Cat6Total = Number(scope3Summary?.by_category?.[6] || 0);
  const scope3Cat7Total = Number(scope3Summary?.by_category?.[7] || 0);

  // Theme-aware, validated palette (charts/chartKit). Colour follows the entity, never its rank:
  // ranked single-measure bars (goods, corridors) use ONE hue; categories and modes use fixed slots.
  const palette = usePalette();
  const CHART_COLORS_EXTENDED = Array(15).fill(palette.single);
  const TRANSPORT_COLORS = {
    road: palette.mode('road'),
    rail: palette.mode('rail'),
    air: palette.mode('air'),
    water: palette.mode('water'),
  };
  const GHG_COLORS = {
    upstream: palette.category(4),
    downstream: palette.category(9),
    company_owned: palette.slot(4),
  };

  // Load scatter plot data
  useEffect(() => {
    const controller = new AbortController();
    const loadScatterData = async () => {
      try {
        const response = await axios.get(`${API}/shipments/scatter-analytics`, { signal: controller.signal });
        setScatterData(response.data);
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Error loading scatter data:', error);
          toast.error('Could not load analytics data');
        }
      }
    };

    if (shipments.length > 0) {
      loadScatterData();
    }
    return () => controller.abort();
  }, [shipments]);

  // Filter shipments by date range
  const getFilteredShipments = () => {
    if (dateRange === 'all') return shipments;

    const now = new Date();
    let startDate;

    switch (dateRange) {
      case '30days':
        startDate = subDays(now, 30);
        break;
      case '90days':
        startDate = subDays(now, 90);
        break;
      case '1year':
        startDate = subYears(now, 1);
        break;
      default:
        return shipments;
    }

    return shipments.filter(s => new Date(s.created_at) >= startDate);
  };

  const filteredShipments = getFilteredShipments();

  const handleSelectShipment = (shipmentId, checked) => {
    setSelectedShipments(prev => {
      if (checked) {
        return [...prev, shipmentId];
      } else {
        return prev.filter(id => id !== shipmentId);
      }
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedShipments(shipments.map(s => s.id));
    } else {
      setSelectedShipments([]);
    }
  };

  const toggleCategoryFilter = (category) => {
    setCategoryFilters(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleBulkDelete = async () => {
    if (selectedShipments.length === 0) {
      toast.error('Please select shipments to delete');
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API}/shipments/bulk`, {
        data: { shipment_ids: selectedShipments }
      });

      toast.success(`Successfully deleted ${selectedShipments.length} shipments`);
      setSelectedShipments([]);
      onShipmentsChange();
    } catch (error) {
      console.error('Error deleting shipments:', error);
      toast.error('Error deleting shipments');
    } finally {
      setLoading(false);
    }
  };

  // ==================== CSV EXPORT FUNCTION ====================
  const exportToCSV = () => {
    if (!filteredShipments || filteredShipments.length === 0) {
      toast.error('No shipment data to export');
      return;
    }

    try {
      // Flatten shipment data for CSV export
      const csvRows = [];

      // CSV Headers
      const headers = [
        'Shipment ID',
        'Date',
        'Product Name',
        'Quantity',
        'Unit',
        'GHG Category',
        'Transport Mode',
        'Vehicle Type',
        'Origin',
        'Destination',
        'Distance (km)',
        'Cost Type',
        'Cost Value (₹)',
        'Total Distance (km)',
        'Total Cost (₹)',
        'Total Emissions (kg CO₂e)',
        'Upstream Emissions (kg CO₂e)',
        'Downstream Emissions (kg CO₂e)',
        'Company Owned Emissions (kg CO₂e)'
      ];
      csvRows.push(headers.join(','));

      // Process each shipment
      filteredShipments.forEach(shipment => {
        const shipmentDate = shipment.created_at
          ? format(new Date(shipment.created_at), 'yyyy-MM-dd HH:mm:ss')
          : '';

        // If shipment has transport legs, create a row for each leg
        if (shipment.transport_legs && shipment.transport_legs.length > 0) {
          shipment.transport_legs.forEach((leg, index) => {
            const row = [
              `"${shipment.id}"`,
              `"${shipmentDate}"`,
              `"${(shipment.good?.name || 'N/A').replace(/"/g, '""')}"`,
              shipment.good?.quantity || 0,
              `"${shipment.good?.unit || 'N/A'}"`,
              `"${(shipment.good?.ghg_category || 'N/A').replace(/_/g, ' ')}"`,
              `"${(leg.transport_mode || 'N/A').charAt(0).toUpperCase() + (leg.transport_mode || 'N/A').slice(1)}"`,
              `"${(leg.vehicle_type || 'N/A').replace(/"/g, '""')}"`,
              `"${(leg.from_location?.address || 'N/A').replace(/"/g, '""')}"`,
              `"${(leg.to_location?.address || 'N/A').replace(/"/g, '""')}"`,
              (leg.distance_km || 0).toFixed(2),
              `"${leg.cost_type || 'N/A'}"`,
              (leg.cost_value || 0).toFixed(2),
              // Include totals only on first leg row
              index === 0 ? (shipment.total_distance || 0).toFixed(2) : '',
              index === 0 ? (shipment.total_cost || 0).toFixed(2) : '',
              index === 0 ? (shipment.total_emissions || 0).toFixed(4) : '',
              index === 0 ? (shipment.upstream_emissions || 0).toFixed(4) : '',
              index === 0 ? (shipment.downstream_emissions || 0).toFixed(4) : '',
              index === 0 ? (shipment.company_owned_emissions || 0).toFixed(4) : ''
            ];
            csvRows.push(row.join(','));
          });
        } else {
          // Shipment without transport legs
          const row = [
            `"${shipment.id}"`,
            `"${shipmentDate}"`,
            `"${(shipment.good?.name || 'N/A').replace(/"/g, '""')}"`,
            shipment.good?.quantity || 0,
            `"${shipment.good?.unit || 'N/A'}"`,
            `"${(shipment.good?.ghg_category || 'N/A').replace(/_/g, ' ')}"`,
            'N/A',
            'N/A',
            'N/A',
            'N/A',
            0,
            'N/A',
            0,
            (shipment.total_distance || 0).toFixed(2),
            (shipment.total_cost || 0).toFixed(2),
            (shipment.total_emissions || 0).toFixed(4),
            (shipment.upstream_emissions || 0).toFixed(4),
            (shipment.downstream_emissions || 0).toFixed(4),
            (shipment.company_owned_emissions || 0).toFixed(4)
          ];
          csvRows.push(row.join(','));
        }
      });

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      // Generate filename with current date
      const dateStr = format(new Date(), 'yyyy-MM-dd_HHmmss');
      const filename = `CarbonRoute_Shipments_${dateStr}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${filteredShipments.length} shipments to CSV`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV file');
    }
  };

  // ==================== DATA PREPARATION FUNCTIONS ====================

  // Prepare data for goods comparison pie chart
  const prepareGoodsPieData = () => {
    if (!filteredShipments || filteredShipments.length === 0) return [];

    const goodsData = {};
    filteredShipments.forEach((shipment) => {
      const goodName = shipment.good?.name || 'Unknown';
      if (!goodsData[goodName]) {
        goodsData[goodName] = { name: goodName, emissions: 0, cost: 0, count: 0 };
      }
      goodsData[goodName].emissions += shipment.total_emissions || 0;
      goodsData[goodName].cost += shipment.total_cost || 0;
      goodsData[goodName].count += 1;
    });

    return Object.values(goodsData)
      .sort((a, b) => b.emissions - a.emissions)
      .map((item, index) => ({
        ...item,
        emissions: Math.round(item.emissions * 100) / 100,
        fill: CHART_COLORS_EXTENDED[index % CHART_COLORS_EXTENDED.length]
      }));
  };

  // Prepare data for transport mode donut chart
  const prepareTransportModeData = () => {
    if (!filteredShipments || filteredShipments.length === 0) return [];

    const modeData = { road: 0, rail: 0, air: 0, water: 0 };
    const modeDistance = { road: 0, rail: 0, air: 0, water: 0 };

    filteredShipments.forEach(shipment => {
      shipment.transport_legs?.forEach(leg => {
        const mode = leg.transport_mode;
        if (modeData[mode] !== undefined) {
          modeData[mode] += leg.emissions || 0;
          modeDistance[mode] += leg.distance_km || 0;
        }
      });
    });

    return Object.entries(modeData)
      .filter(([_, value]) => value > 0)
      .map(([mode, emissions]) => ({
        name: mode.charAt(0).toUpperCase() + mode.slice(1),
        value: Math.round(emissions * 100) / 100,
        distance: Math.round(modeDistance[mode]),
        fill: TRANSPORT_COLORS[mode]
      }));
  };

  // Prepare monthly trend data
  const prepareMonthlyTrendData = () => {
    if (!shipments || shipments.length === 0) return [];

    // Get last 12 months
    const now = new Date();
    const months = eachMonthOfInterval({
      start: subMonths(now, 11),
      end: now
    });

    const monthlyData = months.map(month => {
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);

      const monthShipments = shipments.filter(s => {
        const date = new Date(s.created_at);
        return date >= monthStart && date <= monthEnd;
      });

      return {
        month: format(month, 'MMM yyyy'),
        shortMonth: format(month, 'MMM'),
        upstream: monthShipments.reduce((sum, s) => sum + (s.upstream_emissions || 0), 0),
        downstream: monthShipments.reduce((sum, s) => sum + (s.downstream_emissions || 0), 0),
        company_owned: monthShipments.reduce((sum, s) => sum + (s.company_owned_emissions || 0), 0),
        total: monthShipments.reduce((sum, s) => sum + (s.total_emissions || 0), 0),
        shipments: monthShipments.length,
        cost: monthShipments.reduce((sum, s) => sum + (s.total_cost || 0), 0)
      };
    });

    return monthlyData;
  };

  // Prepare top corridors data
  const prepareTopCorridorsData = () => {
    if (!filteredShipments || filteredShipments.length === 0) return [];

    const corridors = {};

    filteredShipments.forEach(shipment => {
      shipment.transport_legs?.forEach(leg => {
        const from = leg.from_location?.address?.split(',')[0] || 'Unknown';
        const to = leg.to_location?.address?.split(',')[0] || 'Unknown';
        const corridorKey = `${from} → ${to}`;

        if (!corridors[corridorKey]) {
          corridors[corridorKey] = {
            name: corridorKey,
            emissions: 0,
            distance: 0,
            count: 0,
            mode: leg.transport_mode
          };
        }
        corridors[corridorKey].emissions += leg.emissions || 0;
        corridors[corridorKey].distance += leg.distance_km || 0;
        corridors[corridorKey].count += 1;
      });
    });

    return Object.values(corridors)
      .sort((a, b) => b.emissions - a.emissions)
      .slice(0, 10)
      .map((item, index) => ({
        ...item,
        emissions: Math.round(item.emissions * 100) / 100,
        fill: CHART_COLORS_EXTENDED[index % CHART_COLORS_EXTENDED.length]
      }));
  };

  // Prepare GHG category breakdown
  const prepareGHGCategoryData = () => {
    const totals = calculateTotals();
    return [
      { name: 'Upstream (Scope 3 Cat 4)', value: totals.upstreamEmissions, fill: GHG_COLORS.upstream },
      { name: 'Downstream (Scope 3 Cat 9)', value: totals.downstreamEmissions, fill: GHG_COLORS.downstream }
    ].filter(item => item.value > 0);
  };

  // Prepare cumulative emissions data
  const prepareCumulativeData = () => {
    const monthlyData = prepareMonthlyTrendData();
    let cumulative = 0;
    return monthlyData.map(item => {
      cumulative += item.total;
      return { ...item, cumulative: Math.round(cumulative * 100) / 100 };
    });
  };

  // Calculate totals
  const calculateTotals = () => {
    const data = filteredShipments;
    return {
      totalShipments: data.length,
      totalCost: data.reduce((sum, s) => sum + (s.total_cost || 0), 0),
      totalEmissions: data.reduce((sum, s) => sum + (s.total_emissions || 0), 0),
      upstreamEmissions: data.reduce((sum, s) => sum + (s.upstream_emissions || 0), 0),
      downstreamEmissions: data.reduce((sum, s) => sum + (s.downstream_emissions || 0), 0),
      companyOwnedEmissions: data.reduce((sum, s) => sum + (s.company_owned_emissions || 0), 0),
      totalDistance: data.reduce((sum, s) => sum + (s.total_distance || 0), 0)
    };
  };

  const totals = calculateTotals();
  const goodsPieData = prepareGoodsPieData();
  const transportModeData = prepareTransportModeData();
  const monthlyTrendData = prepareMonthlyTrendData();
  const topCorridorsData = prepareTopCorridorsData();
  const ghgCategoryData = prepareGHGCategoryData();
  const cumulativeData = prepareCumulativeData();

  // Custom tooltip for pie charts
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-800">{data.name}</p>
          <p className="text-sm text-gray-600">Emissions: <span className="font-bold text-orange-600">{data.value?.toFixed(2) || data.emissions?.toFixed(2)} kg CO₂</span></p>
          {data.count && <p className="text-xs text-gray-500">{data.count} shipment(s)</p>}
          {data.distance && <p className="text-xs text-gray-500">{data.distance} km total</p>}
        </div>
      );
    }
    return null;
  };

  // Show loading state
  if (loadingShipments) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading your shipment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-dash space-y-6">
      <DashboardHeader
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        onExportCsv={exportToCSV}
        exportDisabled={!filteredShipments || filteredShipments.length === 0}
      />

      <MetricsGrid totals={totals} scope3Summary={scope3Summary} />

      {/* ----- Scope 3 Breakdown ----- */}
      <Scope3Breakdown
        totals={totals}
        cat6Total={scope3Cat6Total}
        cat7Total={scope3Cat7Total}
        activities={scope3Activities}
        onDelete={handleDeleteActivity}
      />

      <ChartTabs activeChart={activeChart} onChange={setActiveChart} />

      {/* Charts Section */}
      {filteredShipments.length > 0 ? (
        <div className="space-y-6">
          {/* OVERVIEW TAB */}
          {activeChart === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="overflow-hidden">
                <ChartHeader title="Freight emissions by GHG category" description="Scope 3 Category 4 (upstream) and 9 (downstream)" />
                <CardContent className="pt-5">
                  <ShareList
                    items={ghgCategoryData.map((d) => ({ key: d.name, name: d.name, value: d.value, color: d.fill }))}
                  />
                </CardContent>
              </Card>

              {/* Transport Mode Distribution */}
              <Card className="overflow-hidden">
                <ChartHeader title="Freight emissions by transport mode" description="Road, rail, air and sea" />
                <CardContent className="pt-5">
                  <ShareList
                    items={transportModeData.map((d) => ({ key: d.name, name: d.name, value: d.value, color: d.fill }))}
                  />
                </CardContent>
              </Card>

              {/* Cost and emissions by category — two single-axis charts. This was ONE chart with two
                  y-scales (₹ and kg), which invites comparing bar heights that share no scale. */}
              {(() => {
                const costData = [
                  { category: 'Cat 4 · Upstream', cat: 4, cost: totals.upstreamEmissions > 0 ? (totals.totalCost * totals.upstreamEmissions / totals.totalEmissions) : 0, emissions: totals.upstreamEmissions },
                  { category: 'Cat 9 · Downstream', cat: 9, cost: totals.downstreamEmissions > 0 ? (totals.totalCost * totals.downstreamEmissions / totals.totalEmissions) : 0, emissions: totals.downstreamEmissions },
                ];
                const panel = (key, title, fmt) => (
                  <Card className="overflow-hidden" key={key}>
                    <ChartHeader title={title} />
                    <CardContent className="pt-4">
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={costData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="35%">
                          <CartesianGrid vertical={false} />
                          <XAxis dataKey="category" {...axisProps} />
                          <YAxis {...axisProps} tickFormatter={fmtAxis} width={48} />
                          <Tooltip
                            cursor={{ fill: 'hsl(var(--muted))' }}
                            content={<ChartTooltip rows={(p) => [{ label: title, value: fmt(p[0].payload[key]), color: palette.category(p[0].payload.cat) }]}
                                                   title={(p) => p[0].payload.category} />}
                          />
                          <Bar isAnimationActive={palette.animate} dataKey={key} radius={[4, 4, 0, 0]} maxBarSize={72}>
                            {costData.map((d) => <Cell key={d.cat} fill={palette.category(d.cat)} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                );
                return (
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:col-span-2">
                    {panel('cost', 'Freight cost by category (₹)', (v) => `₹${fmtNumber(v, 2)}`)}
                    {panel('emissions', 'Freight emissions by category (kg CO₂e)', (v) => `${fmtNumber(v)} kg`)}
                  </div>
                );
              })()}
            </div>
          )}

          {/* TRENDS TAB */}
          {activeChart === 'trends' && (
            <div className="grid grid-cols-1 gap-6">
              {/* Monthly Emissions Trend Line Chart */}
              <Card className="overflow-hidden">
                <ChartHeader title="Monthly freight emissions" description="Last 12 months · kg CO₂e" />
                <CardContent className="pt-4">
                  <ResponsiveContainer width="100%" height={340}>
                    <LineChart data={monthlyTrendData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="shortMonth" {...axisProps} />
                      <YAxis {...axisProps} tickFormatter={fmtAxis} width={48} />
                      <Tooltip
                        cursor={{ stroke: 'hsl(var(--border))' }}
                        content={<ChartTooltip
                          title={(p) => p[0]?.payload?.month}
                          rows={(p) => {
                            const d = p[0]?.payload || {};
                            return [
                              { label: 'Cat 4 · Upstream', value: `${fmtNumber(d.upstream)} kg`, color: palette.category(4) },
                              { label: 'Cat 9 · Downstream', value: `${fmtNumber(d.downstream)} kg`, color: palette.category(9) },
                              { label: 'Total', value: `${fmtNumber(d.total)} kg` },
                              { label: 'Shipments', value: (d.shipments || 0).toLocaleString() },
                            ];
                          }} />}
                      />
                      <Legend iconType="plainline" wrapperStyle={{ paddingTop: 12, fontSize: 12 }} />
                      <Line isAnimationActive={palette.animate} type="monotone" dataKey="upstream" stroke={palette.category(4)} strokeWidth={2} dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: 'hsl(var(--card))' }} name="Cat 4 · Upstream" />
                      <Line isAnimationActive={palette.animate} type="monotone" dataKey="downstream" stroke={palette.category(9)} strokeWidth={2} dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: 'hsl(var(--card))' }} name="Cat 9 · Downstream" />
                      <Line isAnimationActive={palette.animate} type="monotone" dataKey="total" stroke={palette.dark ? '#d1d5db' : '#374151'} strokeDasharray="5 4" strokeWidth={2} dot={false} activeDot={{ r: 5 }} name="Total" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Cumulative Emissions Area Chart */}
              <Card className="overflow-hidden">
                <ChartHeader title="Cumulative freight emissions" description="Running total over the last 12 months · kg CO₂e" />
                <CardContent className="pt-4">
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={cumulativeData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorCumulative" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={palette.single} stopOpacity={0.28} />
                          <stop offset="100%" stopColor={palette.single} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="shortMonth" {...axisProps} />
                      <YAxis {...axisProps} tickFormatter={fmtAxis} width={48} />
                      <Tooltip
                        cursor={{ stroke: 'hsl(var(--border))' }}
                        content={<ChartTooltip
                          title={(p) => p[0]?.payload?.month}
                          rows={(p) => [
                            { label: 'Cumulative', value: `${fmtNumber(p[0]?.payload?.cumulative)} kg`, color: palette.single },
                            { label: 'This month', value: `${fmtNumber(p[0]?.payload?.total)} kg` },
                          ]} />}
                      />
                      <Area isAnimationActive={palette.animate} type="monotone" dataKey="cumulative" stroke={palette.single} strokeWidth={2} fill="url(#colorCumulative)" name="Cumulative" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Stacked Bar - Monthly by Category */}
              <Card className="overflow-hidden">
                <ChartHeader title="Monthly freight emissions by category" description="Stacked · kg CO₂e" />
                <CardContent className="pt-4">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthlyTrendData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }} barCategoryGap="30%">
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="shortMonth" {...axisProps} />
                      <YAxis {...axisProps} tickFormatter={fmtAxis} width={48} />
                      <Tooltip
                        cursor={{ fill: 'hsl(var(--muted))' }}
                        content={<ChartTooltip
                          title={(p) => p[0]?.payload?.month}
                          rows={(p) => [
                            { label: 'Cat 4 · Upstream', value: `${fmtNumber(p[0]?.payload?.upstream)} kg`, color: palette.category(4) },
                            { label: 'Cat 9 · Downstream', value: `${fmtNumber(p[0]?.payload?.downstream)} kg`, color: palette.category(9) },
                          ]} />}
                      />
                      <Legend iconType="square" wrapperStyle={{ paddingTop: 12, fontSize: 12 }} />
                      <Bar isAnimationActive={palette.animate} dataKey="upstream" stackId="a" fill={palette.category(4)} name="Cat 4 · Upstream" stroke="hsl(var(--card))" strokeWidth={2} />
                      <Bar isAnimationActive={palette.animate} dataKey="downstream" stackId="a" fill={palette.category(9)} name="Cat 9 · Downstream" radius={[4, 4, 0, 0]} stroke="hsl(var(--card))" strokeWidth={2} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}

          {/* GOODS TAB */}
          {activeChart === 'goods' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Goods Pie Chart */}
              <Card className="overflow-hidden">
                <ChartHeader title="Share of freight emissions by goods" description="Top 8 goods; the rest are grouped as Other" />
                <CardContent className="pt-5">
                  <ShareList
                    items={(() => {
                      const top = goodsPieData.slice(0, 8).map((g) => ({ key: g.name, name: g.name, value: g.emissions }));
                      const rest = goodsPieData.slice(8).reduce((s, g) => s + (g.emissions || 0), 0);
                      return rest > 0 ? [...top, { key: '__other', name: `Other (${goodsPieData.length - 8})`, value: rest }] : top;
                    })()}
                    total={totals.totalEmissions}
                  />
                </CardContent>
              </Card>

              {/* Goods Bar Chart */}
              <Card className="overflow-hidden">
                <ChartHeader title="Top goods by emissions" description="Ranked · kg CO₂e" />
                <CardContent className="pt-4">
                  <ResponsiveContainer width="100%" height={Math.max(220, Math.min(8, goodsPieData.length) * 40 + 24)}>
                    <BarChart data={goodsPieData.slice(0, 8)} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }} barCategoryGap="28%">
                      <CartesianGrid horizontal={false} />
                      <XAxis type="number" {...axisProps} tickFormatter={fmtAxis} />
                      <YAxis dataKey="name" type="category" {...axisProps} width={120}
                             tickFormatter={(v) => (String(v).length > 16 ? `${String(v).slice(0, 15)}…` : v)} />
                      <Tooltip
                        cursor={{ fill: 'hsl(var(--muted))' }}
                        content={<ChartTooltip
                          title={(p) => p[0]?.payload?.name}
                          rows={(p) => {
                            const d = p[0]?.payload || {};
                            return [
                              { label: 'Emissions', value: `${fmtNumber(d.emissions)} kg`, color: palette.single },
                              { label: 'Cost', value: `₹${fmtNumber(d.cost, 2)}` },
                              { label: 'Shipments', value: (d.count || 0).toLocaleString() },
                            ];
                          }} />}
                      />
                      <Bar isAnimationActive={palette.animate} dataKey="emissions" name="Emissions (kg CO₂e)" fill={palette.single} radius={[0, 4, 4, 0]} maxBarSize={22} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Goods Table */}
              <Card className="pro-card lg:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold text-gray-800">Detailed Goods Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-left p-3 font-semibold text-gray-700">Product/Goods</th>
                          <th className="text-right p-3 font-semibold text-gray-700">Shipments</th>
                          <th className="text-right p-3 font-semibold text-gray-700">Emissions (kg CO₂)</th>
                          <th className="text-right p-3 font-semibold text-gray-700">Cost (₹)</th>
                          <th className="text-right p-3 font-semibold text-gray-700">Cost/kg CO₂</th>
                          <th className="text-right p-3 font-semibold text-gray-700">% of Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {goodsPieData.map((item, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="p-3 font-medium text-gray-800 flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.fill }}></div>
                              {item.name}
                            </td>
                            <td className="text-right p-3 text-gray-600">{item.count}</td>
                            <td className="text-right p-3 text-orange-600 font-medium">{item.emissions.toFixed(2)}</td>
                            <td className="text-right p-3 text-emerald-600 font-medium">₹{item.cost?.toFixed(2)}</td>
                            <td className="text-right p-3 text-gray-600">₹{(item.cost / item.emissions).toFixed(2)}</td>
                            <td className="text-right p-3 text-gray-600">{((item.emissions / totals.totalEmissions) * 100).toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* TRANSPORT TAB */}
          {activeChart === 'transport' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Transport Mode Pie */}
              <Card className="overflow-hidden lg:col-span-2">
                <ChartHeader title="Share of freight emissions by mode" description="Road, rail, air and sea" />
                <CardContent className="pt-5">
                  <ShareList items={transportModeData.map((d) => ({ key: d.name, name: d.name, value: d.value, color: d.fill }))} />
                </CardContent>
              </Card>

              {/* Emissions and distance by mode — two single-axis charts (was one dual-axis chart). */}
              {[
                { key: 'value', title: 'Emissions by mode (kg CO₂e)', fmt: (v) => `${fmtNumber(v)} kg` },
                { key: 'distance', title: 'Distance by mode (km)', fmt: (v) => `${fmtNumber(v, 0)} km` },
              ].map(({ key, title, fmt }) => (
                <Card className="overflow-hidden" key={key}>
                  <ChartHeader title={title} />
                  <CardContent className="pt-4">
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={transportModeData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="35%">
                        <CartesianGrid vertical={false} />
                        <XAxis dataKey="name" {...axisProps} />
                        <YAxis {...axisProps} tickFormatter={fmtAxis} width={48} />
                        <Tooltip
                          cursor={{ fill: 'hsl(var(--muted))' }}
                          content={<ChartTooltip title={(p) => p[0]?.payload?.name}
                                                 rows={(p) => [{ label: title.split(' (')[0], value: fmt(p[0]?.payload?.[key]), color: p[0]?.payload?.fill }]} />}
                        />
                        <Bar isAnimationActive={palette.animate} dataKey={key} radius={[4, 4, 0, 0]} maxBarSize={64}>
                          {transportModeData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              ))}

              {/* Transport Mode Cards */}
              <Card className="lg:col-span-2">
                <ChartHeader title="Transport mode summary" />
                <CardContent className="pt-5">
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4 sm:gap-4">
                    {[
                      { mode: 'road', icon: Truck, label: 'Road' },
                      { mode: 'rail', icon: Train, label: 'Rail' },
                      { mode: 'air', icon: Plane, label: 'Air' },
                      { mode: 'water', icon: Ship, label: 'Sea' }
                    ].map(({ mode, icon: Icon, label }) => {
                      const data = transportModeData.find(d => d.name.toLowerCase() === mode) || { value: 0, distance: 0 };
                      return (
                        <div key={mode} className="glass-inset rounded-xl border border-border bg-muted/40 p-4">
                          <div className="mb-3 flex items-center gap-2.5">
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-card shadow-sm">
                              <Icon className="h-4 w-4" style={{ color: TRANSPORT_COLORS[mode] }} aria-hidden="true" />
                            </span>
                            <span className="text-sm font-semibold text-foreground">{label}</span>
                          </div>
                          <p className="text-xl font-semibold tabular-nums text-foreground">
                            {fmtNumber(data.value || 0)} <span className="text-xs font-normal text-muted-foreground">kg CO₂e</span>
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground tabular-nums">
                            {fmtNumber(data.distance || 0, 0)} km · {fmtShare(data.value || 0, totals.totalEmissions)} of total
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* CORRIDORS TAB */}
          {activeChart === 'corridors' && (
            <div className="grid grid-cols-1 gap-6">
              {/* Top Corridors Horizontal Bar */}
              <Card className="overflow-hidden">
                <ChartHeader title="Top 10 emission corridors" description="Your highest-emitting routes · kg CO₂e" />
                <CardContent className="pt-4">
                  <ResponsiveContainer width="100%" height={Math.max(240, topCorridorsData.length * 38 + 24)}>
                    <BarChart data={topCorridorsData} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }} barCategoryGap="28%">
                      <CartesianGrid horizontal={false} />
                      <XAxis type="number" {...axisProps} tickFormatter={fmtAxis} />
                      <YAxis dataKey="name" type="category" {...axisProps} width={170}
                             tickFormatter={(v) => (String(v).length > 26 ? `${String(v).slice(0, 25)}…` : v)} />
                      <Tooltip
                        cursor={{ fill: 'hsl(var(--muted))' }}
                        content={<ChartTooltip
                          title={(p) => p[0]?.payload?.name}
                          rows={(p) => {
                            const d = p[0]?.payload || {};
                            return [
                              { label: 'Mode', value: <span className="capitalize">{d.mode}</span>, color: TRANSPORT_COLORS[d.mode] },
                              { label: 'Emissions', value: `${fmtNumber(d.emissions)} kg` },
                              { label: 'Distance', value: `${fmtNumber(d.distance, 0)} km` },
                              { label: 'Trips', value: (d.count || 0).toLocaleString() },
                            ];
                          }} />}
                      />
                      <Bar isAnimationActive={palette.animate} dataKey="emissions" name="Emissions (kg CO₂e)" fill={palette.single} radius={[0, 4, 4, 0]} maxBarSize={22} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Corridors Table */}
              <Card className="pro-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold text-gray-800">Route Analysis Details</CardTitle>
                  <CardDescription>Detailed breakdown of all transportation corridors</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="relative overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-left p-3 font-semibold text-gray-700">Route</th>
                          <th className="text-center p-3 font-semibold text-gray-700">Mode</th>
                          <th className="text-right p-3 font-semibold text-gray-700">Trips</th>
                          <th className="text-right p-3 font-semibold text-gray-700">Distance (km)</th>
                          <th className="text-right p-3 font-semibold text-gray-700">Emissions (kg CO₂)</th>
                          <th className="text-right p-3 font-semibold text-gray-700">kg CO₂/km</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topCorridorsData.map((corridor, index) => {
                          const ModeIcon = { road: Truck, rail: Train, air: Plane, water: Ship }[corridor.mode] || Truck;
                          return (
                            <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="p-3 font-medium text-gray-800 flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: corridor.fill }}></div>
                                {corridor.name}
                              </td>
                              <td className="text-center p-3">
                                <div className="flex items-center justify-center gap-1">
                                  <ModeIcon className="w-4 h-4" style={{ color: TRANSPORT_COLORS[corridor.mode] }} />
                                  <span className="text-xs capitalize text-gray-600">{corridor.mode}</span>
                                </div>
                              </td>
                              <td className="text-right p-3 text-gray-600">{corridor.count}</td>
                              <td className="text-right p-3 text-blue-600 font-medium">{corridor.distance}</td>
                              <td className="text-right p-3 text-orange-600 font-medium">{corridor.emissions.toFixed(2)}</td>
                              <td className="text-right p-3 text-gray-600">{(corridor.emissions / corridor.distance).toFixed(4)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      ) : (
        <Card className="pro-card">
          <CardContent className="p-12 text-center">
            <BarChart3 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Data Available</h3>
            <p className="text-gray-500">Calculate your first shipment to see analytics and charts here.</p>
          </CardContent>
        </Card>
      )}

      {/* Recent Shipments Table */}
      <Card data-testid="dashboard-section" className="pro-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-800">All Shipments</CardTitle>
          <CardDescription className="text-sm text-gray-600">
            Complete list of calculated shipments with emissions and costs
          </CardDescription>
        </CardHeader>
        <CardContent>
          {shipments.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              No shipments calculated yet. Calculate your first shipment above.
            </div>
          ) : (
            <div className="space-y-4">
              {/* Multi-select Header */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md border border-gray-200">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedShipments.length === shipments.length && shipments.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-sm text-gray-600">
                    {selectedShipments.length > 0
                      ? `${selectedShipments.length} shipment(s) selected`
                      : 'Select all shipments'
                    }
                  </span>
                </div>
                {selectedShipments.length > 0 && (
                  <Button
                    onClick={handleBulkDelete}
                    disabled={loading}
                    variant="destructive"
                    size="sm"
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected ({selectedShipments.length})
                  </Button>
                )}
              </div>

              {shipments.map((shipment) => (
                <Card key={shipment.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <input
                        type="checkbox"
                        checked={selectedShipments.includes(shipment.id)}
                        onChange={(e) => handleSelectShipment(shipment.id, e.target.checked)}
                        className="w-4 h-4 mt-1 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <div className="flex-1">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <h3 className="font-semibold text-lg text-gray-800">{shipment.good.name}</h3>
                            <p className="text-sm text-gray-600">
                              {shipment.good.quantity} {shipment.good.unit} • {shipment.good.ghg_category}
                            </p>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-semibold tracking-tight tabular-nums text-foreground">
                              {shipment.total_distance?.toFixed(1)} km
                            </div>
                            <div className="text-sm text-gray-600">Total Distance</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-semibold tracking-tight tabular-nums text-foreground">
                              ₹{shipment.total_cost?.toFixed(2)}
                            </div>
                            <div className="text-sm text-gray-600">Total Cost</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-semibold tracking-tight tabular-nums text-foreground">
                              {shipment.total_emissions?.toFixed(2)} kg CO₂
                            </div>
                            <div className="text-sm text-gray-600">Total Emissions</div>
                          </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h4 className="font-medium text-gray-700 mb-3">Transport Legs:</h4>

                          <div className="space-y-2">
                            {shipment.transport_legs.map((leg, index) => {
                              const Icon = {
                                road: Truck,
                                rail: Train,
                                air: Plane,
                                water: Ship
                              }[leg.transport_mode];
                              return (
                                <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                                  <Icon className="h-4 w-4 text-gray-600" />
                                  <span>
                                    {leg.from_location.address} → {leg.to_location.address}
                                    ({leg.distance_km?.toFixed(1)} km via {leg.vehicle_type})
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardContent;

// ============================================================================
// Scope 3 Breakdown — per-category pie + per-mode bars + recent activities.
// Cat 4/9 come from `shipments` (aggregated via `totals`), Cat 6/7 come from
// the `ghg_activities` table (fetched in DashboardContent).
// ============================================================================
function Scope3Breakdown({ totals, cat6Total, cat7Total, activities, onDelete }) {
  const palette = usePalette();
  // Per-category totals across all Scope 3 sources we track today
  const categoryData = [
    { key: 4, name: 'Cat 4 · Upstream transport', value: Number(totals.upstreamEmissions || 0), color: palette.category(4) },
    { key: 9, name: 'Cat 9 · Downstream transport', value: Number(totals.downstreamEmissions || 0), color: palette.category(9) },
    { key: 6, name: 'Cat 6 · Business travel', value: Number(cat6Total || 0), color: palette.category(6) },
    { key: 7, name: 'Cat 7 · Employee commute', value: Number(cat7Total || 0), color: palette.category(7) },
  ].filter((c) => c.value > 0);
  const grandTotal = categoryData.reduce((s, c) => s + c.value, 0);

  // Per-mode breakdown for Cat 6 and Cat 7 from the activity records
  const modeBreakdown = (ghgCat) => {
    const acc = {};
    for (const a of activities) {
      if (Number(a.ghg_category) !== ghgCat) continue;
      const mode = a.mode || 'unknown';
      acc[mode] = (acc[mode] || 0) + Number(a.co2e_kg || 0);
    }
    return Object.entries(acc)
      .map(([mode, kg]) => ({ mode, kg: Number(kg.toFixed(3)) }))
      .sort((a, b) => b.kg - a.kg);
  };
  const cat6Modes = modeBreakdown(6);
  const cat7Modes = modeBreakdown(7);

  const recent = [...activities]
    .sort((a, b) => String(b.activity_date || b.created_at || '').localeCompare(String(a.activity_date || a.created_at || '')))
    .slice(0, 10);

  const empty = grandTotal === 0 && activities.length === 0;
  const modeLabel = (m) => (m ? m.charAt(0).toUpperCase() + m.slice(1) : 'Unknown');

  // Ranked per-mode bars: ONE hue per chart (the category's), modes named on the axis.
  const ModeBars = ({ data, cat }) => (
    <ResponsiveContainer width="100%" height={Math.max(160, data.length * 40 + 24)}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }} barCategoryGap="28%">
        <CartesianGrid horizontal={false} />
        <XAxis type="number" {...axisProps} tickFormatter={fmtAxis} />
        <YAxis dataKey="mode" type="category" {...axisProps} width={96} tickFormatter={modeLabel} />
        <Tooltip
          cursor={{ fill: 'hsl(var(--muted))' }}
          content={<ChartTooltip title={(p) => modeLabel(p[0]?.payload?.mode)}
                                 rows={(p) => [{ label: 'Emissions', value: `${fmtNumber(p[0]?.payload?.kg)} kg`, color: palette.category(cat) }]} />}
        />
        <Bar isAnimationActive={palette.animate} dataKey="kg" fill={palette.category(cat)} radius={[0, 4, 4, 0]} maxBarSize={22} />
      </BarChart>
    </ResponsiveContainer>
  );
  if (empty) {
    return (
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="text-base">Scope 3 breakdown</CardTitle>
          <CardDescription>
            No scope 3 activities yet. Log a Cat 4/9 shipment via the calculator, or a Cat 6 business
            travel / Cat 7 commute activity, and you'll see the split here.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <ChartHeader
          title="Scope 3 breakdown by category"
          description={`How your ${fmtNumber(grandTotal)} kg CO₂e of Scope 3 splits across GHG Protocol categories`}
        />
        <CardContent className="pt-5">
          {/* A ranked share list instead of a donut: one category is ~97% of the total, so the
              donut's small slices could never be labelled without the labels piling up. */}
          <ShareList items={categoryData} total={grandTotal} />
        </CardContent>
      </Card>

      {/* Per-mode breakdown for Cat 6 / Cat 7 */}
      {(cat6Modes.length > 0 || cat7Modes.length > 0) && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {cat6Modes.length > 0 && (
            <Card className="overflow-hidden">
              <ChartHeader title="Cat 6 · Business travel by mode"
                           description={`${fmtNumber(cat6Total)} kg CO₂e across ${cat6Modes.length} mode${cat6Modes.length > 1 ? 's' : ''}`} />
              <CardContent className="pt-4"><ModeBars data={cat6Modes} cat={6} /></CardContent>
            </Card>
          )}
          {cat7Modes.length > 0 && (
            <Card className="overflow-hidden">
              <ChartHeader title="Cat 7 · Employee commute by mode"
                           description={`${fmtNumber(cat7Total)} kg CO₂e across ${cat7Modes.length} mode${cat7Modes.length > 1 ? 's' : ''}`} />
              <CardContent className="pt-4"><ModeBars data={cat7Modes} cat={7} /></CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Recent scope 3 activities */}
      {activities.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Recent Scope 3 activities</CardTitle>
            <CardDescription>
              Latest {recent.length} of {activities.length} travel / commute entries
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="px-3 py-2.5 font-medium">Date</th>
                    <th className="px-3 py-2.5 font-medium">Cat</th>
                    <th className="px-3 py-2.5 font-medium">Mode</th>
                    <th className="px-3 py-2.5 font-medium">Details</th>
                    <th className="px-3 py-2.5 text-right font-medium">Activity</th>
                    <th className="px-3 py-2.5 text-right font-medium">kg CO₂e</th>
                    <th className="px-3 py-2.5"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-0 transition-colors hover:bg-muted/50">
                      <td className="whitespace-nowrap px-3 py-3 tabular-nums text-muted-foreground">{a.activity_date || (a.created_at || '').slice(0, 10) || '—'}</td>
                      <td className="px-3 py-3">
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-border px-2 py-0.5 text-xs font-medium text-foreground">
                          <span className="h-2 w-2 rounded-[2px]" style={{ background: palette.category(Number(a.ghg_category)) }} aria-hidden="true" />
                          Cat {a.ghg_category}
                        </span>
                      </td>
                      <td className="px-3 py-3 capitalize text-foreground">{a.mode}</td>
                      <td className="max-w-[420px] px-3 py-3 text-muted-foreground">
                        <span className="line-clamp-2">
                          {a.subtype || ''}
                          {a.fuel_type ? ` · ${a.fuel_type}` : ''}
                          {a.cabin_class ? ` · ${a.cabin_class}` : ''}
                          {a.origin && a.destination ? ` · ${a.origin} → ${a.destination}` : ''}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-muted-foreground">
                        {fmtNumber(a.activity_value || 0)} {a.activity_unit}
                      </td>
                      <td className="px-3 py-3 text-right font-semibold tabular-nums text-foreground">{fmtNumber(a.co2e_kg || 0, 2)}</td>
                      <td className="px-3 py-3 text-right">
                        <Button size="icon" variant="ghost" onClick={() => onDelete?.(a.id)}
                                title="Delete activity" aria-label="Delete activity"
                                className="h-8 w-8 text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

